import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { SiteProvider } from "@/context/SiteContext";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import ScrollToTop from "@/components/layout/ScrollToTop";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Locations from "@/pages/Locations";
import Services from "@/pages/Services";
import Packages from "@/pages/Packages";
import Booking from "@/pages/Booking";
import MavsVolleyball from "@/pages/MavsVolleyball";
import Offers from "@/pages/Offers";
import Events from "@/pages/Events";
import Testimonials from "@/pages/Testimonials";
import FAQ from "@/pages/FAQ";
import Support from "@/pages/Support";
import Contact from "@/pages/Contact";
import LiveVideo from "@/pages/LiveVideo";
import Login from "@/pages/Login";
import Portal from "@/pages/Portal";
import Policy from "@/pages/Policy";
import News from "@/pages/News";
import NotFound from "@/pages/NotFound";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminContentList from "@/pages/admin/AdminContentList";
import AdminContentEditor from "@/pages/admin/AdminContentEditor";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminTickets from "@/pages/admin/AdminTickets";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminSeo from "@/pages/admin/AdminSeo";
import AdminChat from "@/pages/admin/AdminChat";
import AdminPagesList from "@/pages/admin/AdminPagesList";
import AdminPageEditor from "@/pages/admin/AdminPageEditor";
import { Analytics } from "@/components/common/Analytics";
import { Favicon } from "@/components/common/Favicon";
import { Tracker } from "@/components/common/Tracker";
import { SeoManager } from "@/components/common/SeoManager";

const Public = ({ children }) => <PublicLayout>{children}</PublicLayout>;
const Admin = ({ children }) => <ProtectedRoute requireStaff>{children}</ProtectedRoute>;
const AdminOnly = ({ children }) => <ProtectedRoute requireAdmin>{children}</ProtectedRoute>;
const ContentAdmin = ({ children }) => <ProtectedRoute requireRoles={["admin", "editor"]}>{children}</ProtectedRoute>;

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <SiteProvider>
              <ScrollToTop />
              <Analytics />
              <Favicon />
              <Tracker />
              <SeoManager />
              <Routes>
                <Route path="/" element={<Public><Home /></Public>} />
                <Route path="/about" element={<Public><About /></Public>} />
                <Route path="/locations" element={<Public><Locations /></Public>} />
                <Route path="/services" element={<Public><Services /></Public>} />
                <Route path="/packages" element={<Public><Packages /></Public>} />
                <Route path="/booking" element={<Public><Booking /></Public>} />
                <Route path="/mavs-volleyball" element={<Public><MavsVolleyball /></Public>} />
                <Route path="/offers" element={<Public><Offers /></Public>} />
                <Route path="/events" element={<Public><Events /></Public>} />
                <Route path="/testimonials" element={<Public><Testimonials /></Public>} />
                <Route path="/faq" element={<Public><FAQ /></Public>} />
                <Route path="/support" element={<Public><Support /></Public>} />
                <Route path="/contact" element={<Public><Contact /></Public>} />
                <Route path="/live" element={<Public><LiveVideo /></Public>} />
                <Route path="/news" element={<Public><News /></Public>} />
                <Route path="/login" element={<Public><Login /></Public>} />
                <Route path="/portal" element={<Public><Portal /></Public>} />
                <Route path="/policy/:slug" element={<Public><Policy /></Public>} />

                <Route path="/admin" element={<Admin><AdminDashboard /></Admin>} />
                <Route path="/admin/analytics" element={<Admin><AdminAnalytics /></Admin>} />
                <Route path="/admin/seo" element={<ContentAdmin><AdminSeo /></ContentAdmin>} />
                <Route path="/admin/chat" element={<AdminOnly><AdminChat /></AdminOnly>} />
                <Route path="/admin/settings" element={<AdminOnly><AdminSettings /></AdminOnly>} />
                <Route path="/admin/tickets" element={<Admin><AdminTickets /></Admin>} />
                <Route path="/admin/leads" element={<Admin><AdminLeads /></Admin>} />
                <Route path="/admin/pages" element={<ContentAdmin><AdminPagesList /></ContentAdmin>} />
                <Route path="/admin/pages/:key" element={<ContentAdmin><AdminPageEditor /></ContentAdmin>} />
                <Route path="/admin/:type" element={<Admin><AdminContentList /></Admin>} />
                <Route path="/admin/:type/:id" element={<Admin><AdminContentEditor /></Admin>} />

                <Route path="*" element={<Public><NotFound /></Public>} />
              </Routes>
              <Toaster position="top-right" richColors />
            </SiteProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
